import {
  ArrowLeftFill24Icon,
  TownOutline30Icon,
  TracksOutline30Icon,
  TrainOutline30Icon,
  WarningFill30Icon,
} from "@vygruppen/spor-icon-react";
import { Stop } from "features/CenterContent/VehicleDetails/TrainDetails/useExistingTrainCancelledEvents";
import { ACTIONABLE_TRAIN_TYPES } from "features/History/Filter/FilterData";
import { DetailsHeader } from "features/History/HistoryDetail/components/DetailsHeader";
import { TableDetailAccordionButton } from "features/History/HistoryDetail/components/TableDetailAccordionButton";
import {
  TableDetailAccordionPanel,
  TextOutline,
} from "features/History/HistoryDetail/components/TableDetailAccordionPanel";
import {
  Group,
  HISTORY_DETAILS_QUERY_KEY,
  OriginSystem,
  State,
  useHistoryDetail,
} from "features/History/HistoryDetail/fetchHistoryDetail";
import { EventState, stateMap } from "features/History/utils/utils";
import { DepublishInformation } from "features/OperationalInformation/DepublishInformation";
import { TFunction } from "i18next";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import {
  Box,
  DropsButton,
  DropsExpandable,
  Flex,
  SkeletonText,
  Text,
} from "shared/components/Spor";
import { countryCode } from "shared/types/countryCode";
import { AffectedStop } from "shared/types/historyDetail";
import { useDropsRole } from "stores/useDropsRole";
import styled from "styled-components";

const HistoryWrapper = styled.div`
  grid-area: 2 / 1 / 3 / 4;
  box-sizing: border-box;
  width: 100%;
`;

type HistoryDetailProps = {
  eventUuid: string;
  setEventDetailUUID: (uuid: string | null) => void;
};

const userFromOriginSystem = (originSystem: OriginSystem) => {
  const { name } = originSystem;
  const user = originSystem?.user?.split("@")[0];
  return [name, user].filter(Boolean).join(" - ");
};

export const getEventState = (
  state: State,
  type: string,
  translate: TFunction<"translation", undefined>,
): EventState => {
  const isUnhandled = state === "OPEN" && ACTIONABLE_TRAIN_TYPES.includes(type);
  if (isUnhandled) {
    return stateMap(translate).UNHANDLED;
  }
  return stateMap(translate)[state];
};

// NB! This converts empty lists to undefined as well.
function toAffectedStops(stops?: Stop[]): AffectedStop[] | undefined {
  const affectedStops: AffectedStop[] =
    stops?.map((stop) => ({
      jbvId: stop.ids[0].id,
      name: stop.ids[0].name ?? null,
      nsrCode: null,
    })) ?? [];
  return affectedStops.length > 0 ? affectedStops : undefined;
}

export const tagFromGroup = (
  translate: TFunction<"translation", undefined>,
): { [key in Group]: ReactNode } => ({
  TRAIN: (
    <>
      <TrainOutline30Icon height="18px" />
      {translate("Toghendelse")}
    </>
  ),
  INFRASTRUCTURE: (
    <>
      <TracksOutline30Icon height="18px" />
      {translate("Infrastruktur")}
    </>
  ),
  STATION: (
    <>
      <TownOutline30Icon height="18px" />
      {translate("Stasjonshendelse")}
    </>
  ),
  GLOBAL: (
    <>
      <WarningFill30Icon height="18px" />
      {translate("Global hendelse")}
    </>
  ),
  ALTERNATIVE_TRANSPORT: <>Unexpected type: ALTERNATIVE_TRANSPORT</>,
});

const SkeletonHistoryDetail = () => (
  <>
    <SkeletonText width="500px" />

    <DropsExpandable
      mt="50px"
      title={<SkeletonText width="100%" />}
      collapsible
    />
  </>
);

export const HistoryDetail: FC<HistoryDetailProps> = ({
  eventUuid,
  setEventDetailUUID,
}) => {
  const details = useHistoryDetail(eventUuid);

  const { isVaktlederOrDriftledningen, isDriftledningen, isVaktleder } =
    useDropsRole();

  const { t } = useTranslation();

  if (details.status === "pending") {
    return (
      <HistoryWrapper>
        <Box padding="12px" marginX="auto">
          <DropsButton
            marginY="1rem"
            variant="ghost"
            leftIcon={<ArrowLeftFill24Icon />}
            onClick={() => setEventDetailUUID(null)}
          >
            <Text fontWeight="bold">{t("Tilbake")}</Text>
          </DropsButton>
          <SkeletonHistoryDetail />
        </Box>
      </HistoryWrapper>
    );
  }

  if (details.status === "error" || !details.data) {
    return (
      <HistoryWrapper>
        <Box marginTop="32px">
          <FailureMessage />
        </Box>
      </HistoryWrapper>
    );
  }

  const finalVersion = details.data.versions[0];
  // Cannot depublish swedish events as long as TkStatus and OpInfo are not in sync
  const enableDepublishButton =
    finalVersion.isVisible &&
    ((finalVersion.countryCode === countryCode.enum.NO && isVaktleder()) ||
      (finalVersion.countryCode === countryCode.enum.SE && isDriftledningen()));

  return (
    <HistoryWrapper>
      <Box padding="12px" marginX="auto">
        <DropsButton
          marginY="1rem"
          variant="ghost"
          leftIcon={<ArrowLeftFill24Icon />}
          onClick={() => setEventDetailUUID(null)}
        >
          <Text fontWeight="bold">{t("Tilbake")}</Text>
        </DropsButton>
        <TextOutline
          padding="4px 10px 2px 0px"
          fontSize="2xs"
          fontWeight="bold"
          display="flex"
          marginBottom="15px"
        >
          {tagFromGroup(t)[finalVersion.type.group]}
        </TextOutline>
        <Flex alignItems="center" justifyContent="space-between">
          <DetailsHeader information={finalVersion} />
          {isVaktlederOrDriftledningen() && (
            <DepublishInformation
              uuid={finalVersion.uuid}
              disabled={!enableDepublishButton}
              queryKey={[HISTORY_DETAILS_QUERY_KEY, eventUuid]}
            />
          )}
        </Flex>
        <Flex flexDirection="column" gap="12px" mt="24px">
          {details.data?.versions?.map(
            ({
              actionCard,
              distributions,
              visibility,
              originSystem,
              reason: _reason,
              state,
              stretchName,
              affectedStops: _affectedStops,
              stops,
              type,
              updatedAt,
              version,
              uuid,

              relevantTrain,
              relevantStops: _relevantStops,
              relatedEventUuid,
            }) => {
              const affectedStops = _affectedStops ?? stops;
              const reason = _reason?.description?.NOB ?? t("Ingen årsak");

              const relevantStops = toAffectedStops(_relevantStops);
              return (
                <DropsExpandable
                  key={`${uuid}_${version}`}
                  title={
                    <TableDetailAccordionButton
                      version={version}
                      date={updatedAt}
                      state={getEventState(state, type.type, t)}
                      title={type.label ?? "-"}
                      subtitle={stretchName}
                      description={
                        distributions.find((it) =>
                          it.contextName.includes("DROPS"),
                        )?.text.summary ?? "-"
                      }
                      user={userFromOriginSystem(originSystem)}
                    />
                  }
                  collapsible
                >
                  <TableDetailAccordionPanel
                    group={type.group}
                    reason={reason}
                    actionCard={actionCard?.type}
                    visibility={visibility}
                    originSystem={originSystem}
                    distributions={distributions}
                    affectedStops={affectedStops}
                    relevantTrain={relevantTrain}
                    relevantStops={relevantStops}
                    relatedEventUuid={relatedEventUuid}
                    setEventDetailUUID={setEventDetailUUID}
                  />
                </DropsExpandable>
              );
            },
          )}
        </Flex>
      </Box>
    </HistoryWrapper>
  );
};
